import React, { ReactNode, useState, useMemo } from 'react';
import { Button } from "@/ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui-components/ui/dialog";
import { Info, ChevronDown, Database, Layers, HelpCircle, CheckCircle2 } from "lucide-react";
import { 
  entityRegistry, 
  elementAttributeRegistry, 
  elementRegistry
} from '../template-editor/registry';

import { Template } from '../core/template.schemas';

interface TemplateInfoDialogProps {
  /**
   * The trigger element that opens the dialog (e.g., Button, Icon, etc.)
   * Optional when using controlled mode with open/onOpenChange
   */
  trigger?: ReactNode;
  /**
   * Whether the dialog is open (controlled mode)
   */
  open?: boolean;
  /**
   * Callback when dialog open state changes (controlled mode)
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Template to display info for (optional)
   */
  template?: Template | null;
  /**
   * Custom actions to display in the footer (optional)
   */
  actions?: ReactNode;
}

/**
 * Modern Accordion component for FAQ sections with consistent design
 */
const SimpleAccordion: React.FC<{
  title: string;
  children: ReactNode;
  icon?: ReactNode;
}> = ({ title, children, icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-gray-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-all duration-200"
      >
        <div className="flex items-center gap-3">
          {icon && (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
              <div className="text-blue-600">{icon}</div>
            </div>
          )}
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          <div className="p-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to translate media types to German
 */
const translateMediaType = (mediaType: string): string => {
  const translations: Record<string, string> = {
    'text': 'Text',
    'image': 'Bild',
    'date': 'Datum',
    'color': 'Farbe',
    'int': 'Zahl',
    'float': 'Dezimalzahl'
  };
  return translations[mediaType] || mediaType;
};

/**
 * Helper function to generate example values based on attribute key, media type, and entity type
 */
const generateExampleValue = (attributeKey: string, mediaType: string, entityType: string): string => {
  // Entity-specific examples
  const entityExamples: Record<string, Record<string, string>> = {
    'Team': {
      'name': 'FC Bayern München',
      'shortName': 'FCB',
      'logoPath': 'team-logo.png'
    },
    'Player': {
      'name': 'Max Mustermann',
      'number': '9',
      'imagePath': 'player-photo.jpg'
    },
    'Match': {
      'kickoff': '25.01.2025 15:30',
      'competition': 'Bundesliga'
    },
    'Venue': {
      'name': 'Allianz Arena',
      'city': 'München'
    },
    'Organization': {
      'name': 'Deutscher Fußball-Bund',
      'shortName': 'DFB',
      'logoPath': 'dfb-logo.png',
      'primaryColor': '#000000',
      'secondaryColor': '#FFFFFF',
      'website': 'https://dfb.de'
    }
  };

  // Check for entity-specific example first
  if (entityExamples[entityType]?.[attributeKey]) {
    return entityExamples[entityType][attributeKey];
  }

  // Fallback to media type examples
  const mediaTypeExamples: Record<string, string> = {
    'text': 'Beispieltext',
    'image': 'beispiel-bild.jpg',
    'date': '25.01.2025',
    'color': '#FF0000',
    'int': '42',
    'float': '3.14'
  };

  return mediaTypeExamples[mediaType] || 'Beispielwert';
};

/**
 * Dynamic entity data retrieved from registry
 */
const getEntityData = () => {
  const entities = entityRegistry.getAll();
  
  return entities.map((entity: any) => ({
    type: entity.type,
    displayName: entity.ui?.displayName || entity.type,
    description: entity.ui?.description || `${entity.type} Entität`,
    attributes: entity.attributes.map((attr: any) => ({
      key: attr.key,
      label: attr.ui?.label || attr.key,
      type: translateMediaType(attr.mediaType),
      example: generateExampleValue(attr.key, attr.mediaType, entity.type)
    }))
  }));
};

/**
 * Helper function to generate element attribute examples
 */
const generateElementAttributeExample = (attributeKey: string, mediaTypes: string[]): string => {
  const examples: Record<string, string> = {
    'content': 'Spielername oder "Herzlichen Glückwunsch"',
    'color': 'Vereinsfarben oder feste Farbe',
    'source': 'Spielerfoto oder Vereinslogo',
    'fill.color': 'Vereinsfarben oder feste Farbe',
    'stroke.color': 'Kontrastfarbe oder Vereinsfarbe'
  };

  return examples[attributeKey] || `Beispiel für ${attributeKey}`;
};

/**
 * Dynamic element attribute data retrieved from registry
 */
const getElementAttributeData = () => {
  const elementMappings = elementAttributeRegistry.getAll();
  
  return elementMappings.map((mapping: any) => {
    // Get element definition for UI info
    const elementDef = elementRegistry.get(mapping.elementType);
    
    return {
      elementType: mapping.elementType,
      displayName: elementDef?.ui?.displayName || mapping.displayName || `${mapping.elementType} Element`,
      description: elementDef?.ui?.description || `${mapping.displayName || mapping.elementType} für Template-Gestaltung`,
      attributes: mapping.attributes.map((attr: any) => ({
        key: attr.attributeKey,
        label: attr.attributeLabel,
        types: attr.compatibleMediaTypes.map(translateMediaType),
        example: generateElementAttributeExample(attr.attributeKey, attr.compatibleMediaTypes || ['text'])
      }))
    };
  });
};

/**
 * A reusable dialog component that explains how templates work in SportyPost.
 * Provides information about the template creation workflow, placeholder concept,
 * and the difference between static and dynamic content.
 */
export const TemplateInfoDialog: React.FC<TemplateInfoDialogProps> = ({
  trigger,
  open,
  onOpenChange,
  template,
  actions,
}) => {
  // Dynamic data from registry
  const entityData = useMemo(() => getEntityData(), []);
  const elementAttributeData = useMemo(() => getElementAttributeData(), []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}
      
      <DialogContent className="sm:max-w-4xl border-0 shadow-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 border border-blue-100">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Wie funktionieren Templates?
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 mt-0.5">
                Alles was Sie über Templates, Platzhalter und dynamische Inhalte wissen müssen
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* Workflow Timeline */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
              </div>
              <span className="text-sm font-medium text-gray-900">So funktioniert's - ganz einfach erklärt</span>
            </div>
            
            {/* Timeline Container */}
            <div className="relative ml-5">
              {/* Timeline line */}
              <div className="absolute left-0 top-5 bottom-5 w-px bg-slate-200" />
              
              {/* Timeline Steps */}
              <div className="space-y-8">
                {/* Step 1 - Template erstellen */}
                <div className="relative pl-8">
                  {/* Timeline Icon */}
                  <div className="absolute left-px -translate-x-1/2 h-8 w-8 border border-slate-200 flex items-center justify-center rounded-full bg-blue-50 ring-3 ring-white">
                    <span className="font-medium text-sm text-blue-600">1</span>
                  </div>
                  {/* Content */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium tracking-tight text-gray-900">
                      Template erstellen - wie ein Formular gestalten
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm">
                      Stellen Sie sich vor, Sie erstellen ein Formular: Sie wählen ein Format (Instagram Story, Facebook Post, etc.) 
                      und gestalten es mit Texten, Bildern und Formen. Das ist Ihr <strong>Grundgerüst</strong>.
                    </p>
                    <div className="text-xs text-blue-700 bg-blue-50 p-3 rounded-lg border border-blue-100">
                      <strong>Beispiel:</strong> Ein Geburtstags-Post mit Ihrem Vereinslogo und dem Text "Herzlichen Glückwunsch"
                    </div>
                  </div>
                </div>

                {/* Step 2 - Platzhalter hinzufügen */}
                <div className="relative pl-8">
                  {/* Timeline Icon */}
                  <div className="absolute left-px -translate-x-1/2 h-8 w-8 border border-slate-200 flex items-center justify-center rounded-full bg-purple-50 ring-3 ring-white">
                    <span className="font-medium text-sm text-purple-600">2</span>
                  </div>
                  {/* Content */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium tracking-tight text-gray-900">
                      Platzhalter hinzufügen - die "Lücken" markieren
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm">
                      Jetzt markieren Sie, was sich <strong>ändern soll</strong>. Statt "Max Mustermann" schreiben Sie 
                      einen Platzhalter für "Spielername". Das System füllt diese Lücken später automatisch aus.
                    </p>
                    <div className="text-xs text-purple-700 bg-purple-50 p-3 rounded-lg border border-purple-100">
                      <strong>Beispiel:</strong> Aus "Herzlichen Glückwunsch Max!" wird "Herzlichen Glückwunsch [Spielername]!"
                    </div>
                  </div>
                </div>

                {/* Step 3 - Posts generieren */}
                <div className="relative pl-8">
                  {/* Timeline Icon */}
                  <div className="absolute left-px -translate-x-1/2 h-8 w-8 border border-slate-200 flex items-center justify-center rounded-full bg-emerald-50 ring-3 ring-white">
                    <span className="font-medium text-sm text-emerald-600">3</span>
                  </div>
                  {/* Content */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium tracking-tight text-gray-900">
                      Posts generieren - automatisch für alle erstellen
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm">
                      Wählen Sie einen Spieler aus und das System erstellt automatisch einen personalisierten Post. 
                      Ein Template - unendlich viele Posts!
                    </p>
                    <div className="text-xs text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                      <strong>Beispiel:</strong> Wählen Sie "Thomas Müller" → Post wird zu "Herzlichen Glückwunsch Thomas!"
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Placeholder Concept */}
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
              </div>
              <span className="text-sm font-medium text-gray-900">Statisch vs. Dynamisch - der Unterschied</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-all duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 border border-gray-200">
                    <div className="h-2 w-2 rounded-full bg-gray-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">Statische Inhalte</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  <strong>Bleiben immer gleich</strong> - egal für wen Sie den Post erstellen.
                </p>
                <div className="space-y-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-gray-400" />
                    <span>Ihr Vereinslogo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-gray-400" />
                    <span>Feste Texte wie "Herzlichen Glückwunsch"</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-gray-400" />
                    <span>Vereinsfarben und Design-Elemente</span>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 hover:border-blue-300 transition-all duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 border border-blue-200">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                  </div>
                  <span className="text-sm font-medium text-blue-900">Dynamische Inhalte</span>
                </div>
                <p className="text-sm text-blue-700 leading-relaxed mb-4">
                  <strong>Ändern sich automatisch</strong> - je nachdem, wen Sie auswählen.
                </p>
                <div className="space-y-2 text-xs text-blue-700">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-blue-500" />
                    <span>Spielername (Max → Thomas → Lisa)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-blue-500" />
                    <span>Spielerfotos und Rückennummern</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-blue-500" />
                    <span>Spieldaten und Ergebnisse</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Practical Example */}
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
              </div>
              <span className="text-sm font-medium text-gray-900">Praktisches Beispiel</span>
            </div>
            
            <div className="p-5 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-all duration-200">
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900">Geburtstags-Post für alle Spieler</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-gray-100 border border-gray-200 flex-shrink-0 mt-0.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-gray-500" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">Statisch:</span>
                      <span className="text-sm text-gray-600 ml-2">"Herzlichen Glückwunsch zum Geburtstag" + Vereinslogo</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-blue-100 border border-blue-200 flex-shrink-0 mt-0.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">Dynamisch:</span>
                      <span className="text-sm text-gray-600 ml-2">[Spielername] + [Spielerfoto] + [Rückennummer]</span>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-700 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <strong>Ergebnis:</strong> Ein Template → 25 personalisierte Geburtstags-Posts für alle Spieler!
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Accordions */}
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
              </div>
              <span className="text-sm font-medium text-gray-900">Häufige Fragen & Antworten</span>
            </div>
            
            <div className="space-y-3">
              <SimpleAccordion 
                title="Welche Daten kann ich verwenden?" 
                icon={<Database className="h-4 w-4" />}
              >
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    SportyPost stellt Ihnen verschiedene Datenquellen zur Verfügung:
                  </p>
                  
                  <div className="space-y-3">
                    {entityData.map((entity, index) => (
                      <div key={entity.type} className="p-4 rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">{entity.displayName}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">{entity.description}</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-gray-700 mb-2">Verfügbare Eigenschaften:</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {entity.attributes.map((attr: { key: string; label: string; type: string; example: string }) => (
                              <div key={attr.key} className="flex items-center gap-2 text-xs">
                                <span className="text-gray-700">{attr.label}</span>
                                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                                  {attr.type}
                                </span>
                              </div>
                            ))}
                          </div>
                          {entity.attributes[0]?.example && (
                            <div className="mt-3 pt-2 border-t border-gray-100">
                              <span className="text-xs font-medium text-gray-700">Beispiel: </span>
                              <span className="text-xs text-gray-600">{entity.attributes[0].example}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </SimpleAccordion>

              <SimpleAccordion 
                title="Welche Element-Eigenschaften kann ich dynamisch machen?" 
                icon={<Layers className="h-4 w-4" />}
              >
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Je nach Element-Typ können Sie verschiedene Eigenschaften dynamisch gestalten:
                  </p>
                  
                  <div className="space-y-3">
                    {elementAttributeData.map((element, index) => (
                      <div key={element.elementType} className="p-4 rounded-lg border border-gray-200 bg-white">
                        <div className="mb-3">
                          <h4 className="font-medium text-gray-900 text-sm">{element.displayName}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">{element.description}</p>
                        </div>
                        <div className="space-y-3">
                          {element.attributes.map((attr: any) => (
                            <div key={attr.key} className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="text-sm text-gray-700 font-medium">{attr.label}</div>
                                <div className="text-xs text-gray-500 mt-1">{attr.example}</div>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {attr.types.map((type: string) => (
                                  <span key={type} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </SimpleAccordion>

              <SimpleAccordion 
                title="Wie erstelle ich mein erstes Template?" 
                icon={<HelpCircle className="h-4 w-4" />}
              >
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100 text-blue-600 font-semibold text-sm flex-shrink-0">
                        1
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Format wählen</p>
                        <p className="text-xs text-gray-600 mt-0.5">Instagram Story, Facebook Post, etc.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100 text-blue-600 font-semibold text-sm flex-shrink-0">
                        2
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Grunddesign erstellen</p>
                        <p className="text-xs text-gray-600 mt-0.5">Texte, Bilder und Formen hinzufügen</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100 text-blue-600 font-semibold text-sm flex-shrink-0">
                        3
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Platzhalter definieren</p>
                        <p className="text-xs text-gray-600 mt-0.5">Elemente als "dynamisch" markieren</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100 text-blue-600 font-semibold text-sm flex-shrink-0">
                        4
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Template speichern</p>
                        <p className="text-xs text-gray-600 mt-0.5">Fertig! Jetzt können Sie Posts generieren</p>
                      </div>
                    </div>
                  </div>
                </div>
              </SimpleAccordion>

              <SimpleAccordion 
                title="Was ist der Unterschied zu normalen Design-Tools?" 
                icon={<HelpCircle className="h-4 w-4" />}
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg border border-gray-200 bg-white">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Normale Design-Tools</h4>
                      <ul className="text-xs text-gray-600 space-y-2">
                        <li key="design-post" className="flex items-center gap-2">
                          <div className="h-1 w-1 rounded-full bg-gray-400 flex-shrink-0" />
                          Ein Design = Ein Post
                        </li>
                        <li key="manual-creation" className="flex items-center gap-2">
                          <div className="h-1 w-1 rounded-full bg-gray-400 flex-shrink-0" />
                          Jeder Post manuell erstellen
                        </li>
                        <li key="time-consuming" className="flex items-center gap-2">
                          <div className="h-1 w-1 rounded-full bg-gray-400 flex-shrink-0" />
                          Zeitaufwendig bei vielen Posts
                        </li>
                        <li key="error-prone" className="flex items-center gap-2">
                          <div className="h-1 w-1 rounded-full bg-gray-400 flex-shrink-0" />
                          Fehleranfällig bei Wiederholungen
                        </li>
                      </ul>
                    </div>
                    
                    <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                      <h4 className="text-sm font-medium text-blue-900 mb-3">SportyPost Templates</h4>
                      <ul className="text-xs text-blue-700 space-y-2">
                        <li key="template-infinite" className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
                          Ein Template = Unendlich viele Posts
                        </li>
                        <li key="auto-generate" className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
                          Posts automatisch generieren
                        </li>
                        <li key="fast-creation" className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
                          Sekunden statt Stunden
                        </li>
                        <li key="consistent-design" className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
                          Konsistentes Design garantiert
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </SimpleAccordion>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-6 border-t border-gray-100 gap-3">
          {actions ? (
            actions
          ) : (
            <DialogClose asChild>
              <Button
                type="button"
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm transition-all"
              >
                Verstanden
              </Button>
            </DialogClose>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};